import React, { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Line,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  LabelList,
} from "recharts";
import styles from "../../../../../components/barAndLineComboChart/barAndLineComboChart.module.css";

const BAR_WIDTH = 80;
const WINDOW_SIZE = 10;

const EPSILON = 0.0001;

const safeValue = (value) => {
  const numberValue = Number(value);
  return numberValue > 0 ? numberValue : EPSILON;
};

const formatToCappedUnit = (value) => {
  if (value === EPSILON) return "0";
  if (value === null || value === undefined || value === 0) return "0";

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) return "0";

  if (numberValue < 1000) {
    return `${numberValue}`;
  }

  if (numberValue < 100000) {
    return `${(numberValue / 1000).toFixed(1)} K`;
  }

  if (numberValue < 10000000) {
    return `${(numberValue / 100000).toFixed(1)} L`;
  }

  return `${(numberValue / 10000000).toFixed(1)} Cr`;
};

const formatTooltipValue = (value) => {
  if (value === EPSILON) return "0";

  return Number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
};

const getValueByKeyPrefix = (item, prefix) => {
  const matchedKey = Object.keys(item || {}).find((key) =>
    key.toLowerCase().startsWith(prefix.toLowerCase())
  );

  return matchedKey ? item[matchedKey] : 0;
};

const renderTopBarLabel = ({ x, y, width, value }) => {
  if (value === null || value === undefined) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fill="#1f2933"
      fontSize={10}
      fontWeight={700}
    >
      {value === EPSILON ? "0" : formatToCappedUnit(value)}
    </text>
  );
};

const CustomizedAxisTick = ({ x, y, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="middle" fontSize={9} fill="#2B2B2B">
      {payload?.value}
    </text>
  </g>
);

const CompactTooltip = ({ active, payload, label, coordinate }) => {
  const payloadItems = Array.isArray(payload) ? payload : [];

  if (!active || payloadItems[0] === undefined || !coordinate) return null;

  const tooltipWidth = 220;
  const tooltipHeight = 85;

  const container = document.querySelector(`.${styles.scrollbarContainer}`);
  const rect = container?.getBoundingClientRect();
  if (!rect) return null;

  let left = rect.left + coordinate.x + 10;
  let top = rect.top + coordinate.y - tooltipHeight + 15;

  if (left + tooltipWidth > rect.right - 10) {
    left = rect.right - tooltipWidth - 10;
  }

  if (left < rect.left + 10) {
    left = rect.left + 10;
  }

  if (top < rect.top + 10) {
    top = rect.top + coordinate.y + 15;
  }

  if (top + tooltipHeight > rect.bottom) {
    top = rect.bottom - tooltipHeight - 10;
  }

  return (
    <div
      style={{
        position: "fixed",
        left,
        top,
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        padding: "6px 8px",
        fontSize: 10,
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        zIndex: 9999,
        pointerEvents: "none",
        width: tooltipWidth,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>

      {payloadItems.map((entry) => (
        <div key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {formatTooltipValue(entry.value)}
        </div>
      ))}
    </div>
  );
};

const IntrRecoveryGlobalGraph = ({ data = [], topNValue = 10 }) => {
  const [startIndex, setStartIndex] = useState(0);
  const chartContainerRef = useRef(null);

  const visibleData = useMemo(
    () => data.slice(startIndex, startIndex + WINDOW_SIZE),
    [data, startIndex]
  );

  useEffect(() => {
    if (startIndex > Math.max(data.length - WINDOW_SIZE, 0)) {
      setStartIndex(0);
    }
  }, [data.length, startIndex]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const maxStartIndex = Math.max(data.length - WINDOW_SIZE, 0);
      const newStart = Math.floor(container.scrollLeft / BAR_WIDTH);

      if (
        newStart !== startIndex &&
        newStart >= 0 &&
        newStart <= maxStartIndex
      ) {
        setStartIndex(newStart);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, [data.length, startIndex]);

  const transformedData = useMemo(
    () =>
      visibleData.map((item) => {
        const weightedAverageValue = getValueByKeyPrefix(
          item,
          "Order Loss Value Mitigated by Weighted average"
        );

        const fifoValue = getValueByKeyPrefix(
          item,
          "Order Loss Value Mitigated by FIFO"
        );

        return {
          ...item,
          receivingPlant: item["Receiving Plant"],
          weightedAverage: safeValue(weightedAverageValue),
          fifo: safeValue(fifoValue),
          grns: Number(item["Number of GRNs"] || 0),
        };
      }),
    [visibleData]
  );

  const grnMax = Math.max(...transformedData.map((d) => d.grns), 0);
  const rightDomain = [0, Math.ceil(grnMax * 1.2) || 1];

  const isScrollable = data.length > WINDOW_SIZE;

  return (
    <div
      className={styles.scrollbarContainer}
      ref={chartContainerRef}
      style={{
        overflowX: isScrollable ? "auto" : "hidden",
        width: "100%",
        position: "relative",
        marginTop: "5px",
      }}
    >
      <div
        style={{
          width: isScrollable ? `${data.length * BAR_WIDTH}px` : "100%",
          height: 280,
          position: "relative",
          overflow: "visible",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: `${startIndex * BAR_WIDTH}px`,
            width: isScrollable ? `${WINDOW_SIZE * BAR_WIDTH}px` : "100%",
            height: 280,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={transformedData}
              barGap={4}
              barCategoryGap={20}
              margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="receivingPlant"
                tick={<CustomizedAxisTick />}
                interval={0}
                height={50}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
              />

              <YAxis
                yAxisId="left"
                scale="log"
                domain={["dataMin", "dataMax"]}
                tickFormatter={formatToCappedUnit}
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={90}
              />

              <YAxis
                yAxisId="right"
                orientation="right"
                domain={rightDomain}
                tickFormatter={(val) => Math.round(val)}
                allowDecimals={false}
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={55}
              />

              <Tooltip
                content={<CompactTooltip />}
                isAnimationActive={false}
                allowEscapeViewBox={{ x: true, y: true }}
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />

              <Legend
                verticalAlign="bottom"
                align="left"
                wrapperStyle={{ fontSize: "10px", color: "black" }}
              />

              <Bar
                yAxisId="left"
                dataKey="weightedAverage"
                name={`Value Mitigated by Weighted Average Top ${topNValue}`}
                fill="rgb(0, 94, 239)"
                barSize={35}
                minPointSize={2}
              >
                <LabelList content={renderTopBarLabel} />
              </Bar>

              <Bar
                yAxisId="left"
                dataKey="fifo"
                name={`Value Mitigated by FIFO Top ${topNValue}`}
                fill="rgb(0, 177, 144)"
                barSize={35}
                minPointSize={2}
              >
                <LabelList content={renderTopBarLabel} />
              </Bar>

              <Line
                yAxisId="right"
                type="monotone"
                dataKey="grns"
                name="Number of GRNs"
                stroke="#e7381d"
                strokeWidth={2}
                strokeOpacity={0.9}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

IntrRecoveryGlobalGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  topNValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CustomizedAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
};

CompactTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({ dataKey: PropTypes.string, color: PropTypes.string, name: PropTypes.string, value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  coordinate: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
};

export default IntrRecoveryGlobalGraph;